import { Routes, UrlMatcher, UrlSegment } from '@angular/router';
import { SuggestDataService } from '@shared/modals/suggest-data/suggest-data.service';
import { V2_BRIEFING_PROVIDERS } from './domain/v2-briefing.service';

/**
 * A briefing id at the root of the briefings host.
 *
 * The briefing surface serves `/` and `/{id}`, and `/login`, `/faq`, `/terms`
 * and `/contacts` sit beside them at the same depth. A plain `:id` route would
 * match all four of those names. Briefing ids are numeric -- the API keys them
 * by primary key -- so digits are the whole distinction, and stating it here
 * is cheaper than ordering the route table just so and hoping nobody reorders
 * it.
 */
export const briefingIdMatcher: UrlMatcher = (segments: UrlSegment[]) => {
  if (segments.length !== 1 || !/^\d+$/.test(segments[0].path)) {
    return null;
  }
  return { consumed: segments, posParams: { id: segments[0] } };
};

/**
 * The two briefing pages.
 *
 * These are spread into `v2.routes.ts` at the root of the briefings surface
 * rather than mounted under a parent segment -- the host is what says
 * "briefings" now, so a `/briefings` path segment would say it twice.
 *
 * `V2_BRIEFING_PROVIDERS` carries `AirportApiService` and the two store
 * services it depends on; none of them declare `providedIn`, because they are
 * registered on `AirportModule` for the old briefing. `SuggestDataService` is
 * here for the same reason -- it comes from `SuggestDataModule`, and the
 * feedback dialog is opened with this route's injector.
 *
 * Both entries reference the one `V2_BRIEFING_ROUTE_PROVIDERS` array below --
 * written once, so a service added for one page cannot be missing from the
 * other. They do *not* share an injector: Angular keys a route-level
 * `EnvironmentInjector` to the `Route` object, and these are two objects, so
 * `V2BriefingService` and the rest are instantiated once per page. Nothing
 * depends on them being shared; `V2BriefingTabsService`, which the tab strip
 * uses across the trip between the two pages, is `providedIn: 'root'`.
 */
export const V2_BRIEFING_ROUTE_PROVIDERS = [...V2_BRIEFING_PROVIDERS, SuggestDataService];

export const v2BriefingRoutes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    providers: V2_BRIEFING_ROUTE_PROVIDERS,
    loadComponent: () =>
      import('./start/v2-briefing-start.component').then(m => m.V2BriefingStartComponent),
    data: { pageName: 'EVO Apps | Airport Briefings' },
  },
  {
    matcher: briefingIdMatcher,
    providers: V2_BRIEFING_ROUTE_PROVIDERS,
    loadComponent: () =>
      import('./page/v2-briefing-page.component').then(m => m.V2BriefingPageComponent),
    data: { pageName: 'EVO Apps | Airport Briefing' },
  },
];
